import { DashboardOverviewHtml } from "../../components/skins/dashboard-overview.ts";
import { globalState } from "../../framework/globalState.ts";
import { ChartManager } from "../../components/charts/ChartManager.ts";
import { chartStyles } from "../../components/charts/ChartStyles.ts";
import {
  getDashboardWarmSnapshot,
  primeDashboardWarmSnapshot,
  startDashboardWarmPolling,
  type DashboardWarmSnapshot,
} from "../../services/dashboardWarmCache.ts";
import {
  fetchAlarms,
  fetchAlarmCount,
  fetchBatteryAssetSummary,
  fetchGeneratorAssetSummary,
  fetchGridAssetSummary,
  fetchPvAssetSummary,
  fetchWeather,
} from "../../services/api.ts";

export default function DashboardPage() {
  const container = document.createElement("div");
  container.classList.add("parent-container");
  container.classList.add("dashboard-overview-page");
  container.innerHTML = new DashboardOverviewHtml().dashboardoverviewskin();

  const siteWithData = globalState.currentSiteWithData as any;
  const site = siteWithData?.site || siteWithData;
  const registers = (siteWithData?.devices || []).flatMap((d: any) =>
    (d.registers || []).map((r: any) => ({ ...r, deviceName: d.name, deviceId: d._id }))
  );

  if (!site || !registers.length) {
    console.warn("Site or registers not ready.");
    return container;
  }

  const fmt = (value: unknown, unit = '', digits = 1) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return '—';
    return `${n.toFixed(digits)}${unit ? ` ${unit}` : ''}`;
  };

  const normalize = (value: unknown) => String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');

  const getEl = (key: string) => container.querySelector(`[data-dashboard="${key}"]`) as HTMLElement | null;
  const setProgress = (key: string, percent: number) => {
    const el = container.querySelector(`[data-dashboard-progress="${key}"]`) as HTMLElement | null;
    if (!el) return;
    const p = Math.max(0, Math.min(100, Number(percent) || 0));
    el.style.setProperty('--progress-width', `${p}%`);
  };

  /**
   * Update a KPI spark bar: width = (value - min) / (max - min), clamped 0-100%.
   * Color is green when the reading is "good" (higherIsBetter=true → value ≥ goodThreshold)
   * or red when it's bad, with an amber mid-zone.
   * @param key          data-kpi-spark attribute value
   * @param rawValue     raw numeric value (null/undefined → bar stays empty)
   * @param min          lower bound of expected range
   * @param max          upper bound of expected range
   * @param goodPct      0-100: percentage of range above which bar turns green (default 66)
   * @param warnPct      0-100: percentage of range below which bar turns red  (default 33)
   * @param higherIsBetter  true = green at top, false = green at bottom (e.g. peak demand)
   */
  const setKpiSpark = (
    key: string,
    rawValue: unknown,
    min: number,
    max: number,
    goodPct = 66,
    warnPct = 33,
    higherIsBetter = true,
  ) => {
    const el = container.querySelector(`[data-kpi-spark="${key}"]`) as HTMLElement | null;
    if (!el) return;
    const n = Number(rawValue);
    if (!Number.isFinite(n)) {
      el.style.setProperty('--kpi-bar-w', '0%');
      el.style.setProperty('--kpi-bar-color', '#334155');
      return;
    }
    const range = max - min || 1;
    const ratio = Math.max(0, Math.min(1, (n - min) / range));
    const pct = ratio * 100;
    const goodThreshold = higherIsBetter ? goodPct : 100 - goodPct;
    const warnThreshold = higherIsBetter ? warnPct : 100 - warnPct;
    let color: string;
    if (higherIsBetter) {
      color = pct >= goodPct ? '#22c55e' : pct >= warnPct ? '#f59e0b' : '#ef4444';
    } else {
      // lower is better (e.g. peak demand) — green if below warnPct, red if above goodPct
      color = pct <= warnPct ? '#22c55e' : pct <= goodPct ? '#f59e0b' : '#ef4444';
    }
    el.style.setProperty('--kpi-bar-w', `${pct.toFixed(1)}%`);
    el.style.setProperty('--kpi-bar-color', color);
  };

  const readRegValue = (patterns: string[]): unknown => {
    const normalizedPatterns = patterns.map((p) => normalize(p));
    const reg = registers.find((r: any) => {
      const key = normalize(r?.dictionaryKey);
      const name = normalize(r?.name);
      return normalizedPatterns.some((p) => key.includes(p) || name.includes(p));
    });
    return reg?.value;
  };

  const modeLabel = (value: unknown, on = 'AUTO', off = 'MANUAL') => {
    if (value === true || value === 1 || String(value).toLowerCase() === 'auto') return on;
    if (value === false || value === 0 || String(value).toLowerCase() === 'manual') return off;
    if (value == null || value === '') return '—';
    return String(value).toUpperCase();
  };

  const applyChip = (el: HTMLElement, label: string, bg: string) => {
    el.textContent = label;
    el.style.color = '#ffffff';
    el.style.backgroundColor = bg;
    el.style.padding = '2px 10px';
    el.style.borderRadius = '12px';
    el.style.fontSize = '0.72rem';
    el.style.fontWeight = '600';
    el.style.letterSpacing = '0.02em';
    el.style.display = 'inline-block';
  };

  const applyDashboardSnapshot = (snapshot: DashboardWarmSnapshot) => {
    const { pv, bess, dg, grid, weather, alarms } = snapshot;

    const pvPower = (pv as any)?.activePower ?? (pv as any)?.totalOutput;
    const bessSoc = (bess as any)?.soc ?? (bess as any)?.averageSOC;
    const bessPower = (bess as any)?.batteryPower;
    const bessDischarge = (bess as any)?.dischargePower;
    const gridPower = (grid as any)?.activePower;
    const genPower = (dg as any)?.activePower ?? (dg as any)?.totalOutput;
    const bessGen = bessDischarge && Number(bessDischarge) > 0 ? Number(bessDischarge) :
          bessPower && Number(bessPower) > 0 ? Number(bessPower) : 0;
    const totalGeneration = (Number(pvPower) || 0) + (Number(genPower) || 0) + bessGen;
    const ghi = (weather as any)?.local?.ghi?.value
      ?? (weather as any)?.local?.poa?.value
      ?? (weather as any)?.fallback?.ghi
      ?? (weather as any)?.fallback?.poa
      ?? (weather as any)?.fallback?.irradiance;
    const gridFrequency = (grid as any)?.frequency;
    const gridVoltage = (grid as any)?.voltage;
    const totalLoad = (grid as any)?.loadPower ?? (grid as any)?.totalLoad ?? null;
    const plantEfficiency = (pv as any)?.efficiency ?? (pv as any)?.plantEfficiency ?? null;
    const co2Savings = (pv as any)?.co2Savings ?? null;
    const performanceRatio = (pv as any)?.performanceRatio ?? null;
    const specificYield = (pv as any)?.specificYield ?? null;
    const batteryUtilization = (bess as any)?.utilization ?? (bess as any)?.batteryUtilization ?? null;
    const gridAvailability = (grid as any)?.availability ?? null;
    const peakDemand = (grid as any)?.peakDemand ?? null;
    const peakTime = (grid as any)?.peakAt ?? null;

    const totalGenEl = getEl('totalGeneration');
    if (totalGenEl) totalGenEl.textContent = fmt(totalGeneration, 'kW', 1);
    const pvEl = getEl('pvPower');
    if (pvEl) pvEl.textContent = fmt(pvPower, 'kW', 1);
    const bessEl = getEl('bessSoc');
    if (bessEl) bessEl.textContent = fmt(bessSoc, '%', 1);
    const bessPowerEl = getEl('bessPower');
    if (bessPowerEl) bessPowerEl.textContent = fmt(bessPower, 'kW', 1);
    const gridEl = getEl('gridPower');
    if (gridEl) gridEl.textContent = fmt(gridPower, 'kW', 1);
    const alarmEl = getEl('alarmCount');
    if (alarmEl) alarmEl.textContent = String((alarms as any)?.count ?? 0);
    const ghiEl = getEl('ghi');
    if (ghiEl) ghiEl.textContent = fmt(ghi, 'W/m²', 0);
    const freqEl = getEl('gridFrequency');
    if (freqEl) freqEl.textContent = fmt(gridFrequency, 'Hz', 2);
    const voltEl = getEl('gridVoltage');
    if (voltEl) voltEl.textContent = fmt(gridVoltage, 'kV', 2);

    const loadEl = getEl('totalLoad');
    if (loadEl) loadEl.textContent = fmt(totalLoad, 'kW', 1);
    const effEl = getEl('plantEfficiency');
    if (effEl) effEl.textContent = fmt(plantEfficiency, '%', 1);
    const co2El = getEl('co2Savings');
    if (co2El) co2El.textContent = fmt(co2Savings, 'kg', 1);
    const prEl = getEl('performanceRatio');
    if (prEl) prEl.textContent = fmt(performanceRatio, '%', 1);
    const syEl = getEl('specificYield');
    if (syEl) syEl.textContent = fmt(specificYield, 'kWh/kWp', 2);
    const buEl = getEl('batteryUtilization');
    if (buEl) buEl.textContent = fmt(batteryUtilization, '%', 1);
    const gaEl = getEl('gridAvailability');
    if (gaEl) gaEl.textContent = fmt(gridAvailability, '%', 1);
    const pdEl = getEl('peakDemand');
    if (pdEl) pdEl.textContent = fmt(peakDemand, 'kW', 1);
    const ptEl = getEl('peakTime');
    if (ptEl) ptEl.textContent = peakTime ? new Date(peakTime as any).toLocaleTimeString() : '—';

    setProgress('gridFrequency', gridFrequency ? (Number(gridFrequency) / 60) * 100 : 0);
    setProgress('gridVoltage', gridVoltage ? (Number(gridVoltage) / 11) * 100 : 0);
    setProgress('bessSoc', Number(bessSoc) || 0);
    setProgress('pvPower', pvPower ? Math.min((Number(pvPower) / Math.max(totalGeneration || 1, 1)) * 100, 100) : 0);
    setProgress('bessPower', bessPower ? Math.min((Math.abs(Number(bessPower)) / Math.max(Math.abs(Number(totalGeneration)) || 1, 1)) * 100, 100) : 0);
    setProgress('gridPower', gridPower ? Math.min((Math.abs(Number(gridPower)) / Math.max(Math.abs(Number(totalGeneration)) || 1, 1)) * 100, 100) : 0);

    const genCap = Math.max(Number(peakDemand) * 2 || 0, 1000);
    setKpiSpark('totalGeneration', totalGeneration, 0, genCap, 60, 20, true);
    setKpiSpark('totalLoad', totalLoad, 0, genCap, 80, 40, true);
    setKpiSpark('plantEfficiency', plantEfficiency, 0, 100, 80, 60, true);
    setKpiSpark('co2Savings', co2Savings, 0, 5000, 40, 10, true);
    setKpiSpark('performanceRatio', performanceRatio, 0, 100, 75, 50, true);
    setKpiSpark('specificYield', specificYield, 0, 6, 67, 33, true);
    setKpiSpark('batteryUtilization', batteryUtilization, 0, 100, 50, 20, true);
    setKpiSpark('gridAvailability', gridAvailability, 0, 100, 95, 85, true);
    setKpiSpark('peakDemand', peakDemand, 0, genCap, 70, 30, false);

    setPvStatusChip((pv as any)?.operatingStatus?.isOnline);
    setBessStatusChip((bess as any)?.operatingStatus?.isOnline, bessPower);
    setGenStatusChip((dg as any)?.operatingStatus?.isOnline);
    setGridStatusChip((grid as any)?.operatingStatus?.isOnline, gridPower, (grid as any)?.gridStatusCode);

    const controlModeVal = readRegValue(['control_mode', 'ctrl_mode', 'local_remote']);
    const maintenanceVal = readRegValue(['maintenance_mode', 'maint_mode']);
    const pModeVal = readRegValue(['active_power_mode', 'real_power_mode']);
    const pSetpoint = readRegValue(['active_power_setpoint', 'ppc_sp', 'wp_ppc_sp']);
    const pFeedback = readRegValue(['active_power_feedback', 'ppc_feedback', 'plant_active']);
    const qModeVal = readRegValue(['reactive_power_mode']);
    const qSetpoint = readRegValue(['reactive_power_setpoint']);
    const qFeedback = readRegValue(['reactive_power_feedback']);
    const pfModeVal = readRegValue(['power_factor_mode', 'pf_mode']);
    const pfSetpoint = readRegValue(['power_factor_setpoint']);
    const pfFeedback = readRegValue(['power_factor_feedback', 'meter_power_factor']);

    const controlModeEl = getEl('controlMode');
    if (controlModeEl) controlModeEl.textContent = modeLabel(controlModeVal, 'REMOTE', 'LOCAL');
    const maintenanceModeEl = getEl('maintenanceMode');
    if (maintenanceModeEl) maintenanceModeEl.textContent = modeLabel(maintenanceVal, 'ON', 'OFF');
    const realModeEl = getEl('realPowerMode');
    if (realModeEl) realModeEl.textContent = modeLabel(pModeVal, 'AUTO', 'MANUAL');
    const realSpFbEl = getEl('realPowerSpFb');
    if (realSpFbEl) realSpFbEl.textContent = `${fmt(pSetpoint, 'MW', 2)} / ${fmt(pFeedback, 'MW', 2)}`;
    const reactiveModeEl = getEl('reactiveMode');
    if (reactiveModeEl) reactiveModeEl.textContent = modeLabel(qModeVal, 'AUTO', 'MANUAL');
    const reactiveSpFbEl = getEl('reactiveSpFb');
    if (reactiveSpFbEl) reactiveSpFbEl.textContent = `${fmt(qSetpoint, 'MVAr', 2)} / ${fmt(qFeedback, 'MVAr', 2)}`;
    const pfModeEl = getEl('pfMode');
    if (pfModeEl) pfModeEl.textContent = modeLabel(pfModeVal, 'AUTO', 'MANUAL');
    const pfSpFbEl = getEl('pfSpFb');
    if (pfSpFbEl) pfSpFbEl.textContent = `${fmt(pfSetpoint, '', 3)} / ${fmt(pfFeedback, '', 3)}`;

    const refreshed = getEl('lastRefresh');
    if (refreshed) refreshed.textContent = new Date().toLocaleTimeString();
  };

  let hasShownInitialOverviewLoading = false;
  let initialOverviewRevealTimers: number[] = [];
  let overviewRefreshInFlight = false;
  let queuedOverviewRefresh = false;
  const overviewRequestCache = new Map<string, { expiresAt: number; data: any }>();

  const clearInitialOverviewLoadingProgressively = () => {
    initialOverviewRevealTimers.forEach((timerId) => window.clearTimeout(timerId));
    initialOverviewRevealTimers = [];

    const revealGroups = [
      Array.from(container.querySelectorAll('.dashboard-v2-kpi-card')),
      Array.from(container.querySelectorAll('.dashboard-v2-list > div')),
      Array.from(container.querySelectorAll('.dashboard-panel')),
    ];

    revealGroups.forEach((group, index) => {
      const timerId = window.setTimeout(() => {
        group.forEach((el) => el.classList.remove('is-loading'));
      }, index * 120);
      initialOverviewRevealTimers.push(timerId);
    });
  };

  const delay = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

  const loadOverviewResource = async <T,>(key: string, loader: () => Promise<T>, ttlMs = 4000): Promise<T> => {
    const now = Date.now();
    const cached = overviewRequestCache.get(key);
    if (cached && cached.expiresAt > now) {
      return cached.data as T;
    }

    try {
      const data = await loader();
      overviewRequestCache.set(key, { data, expiresAt: Date.now() + ttlMs });
      return data;
    } catch (error: any) {
      const message = String(error?.message || error || '');
      if (!message.includes('429')) throw error;

      await delay(500);
      const data = await loader();
      overviewRequestCache.set(key, { data, expiresAt: Date.now() + ttlMs });
      return data;
    }
  };

  const loadOverviewSnapshot = async () => {
    const pv = await loadOverviewResource('pv-summary', () => fetchPvAssetSummary(site._id) as Promise<any>);
    const bess = await loadOverviewResource('battery-summary', () => fetchBatteryAssetSummary(site._id) as Promise<any>);
    const dg = await loadOverviewResource('generator-summary', () => fetchGeneratorAssetSummary(site._id) as Promise<any>);
    const grid = await loadOverviewResource('grid-summary', () => fetchGridAssetSummary(site._id) as Promise<any>);
    const weather = await loadOverviewResource('weather-current', () => fetchWeather(site._id) as Promise<any>);
    const alarms = await loadOverviewResource('alarm-count-active', async () => {
      const activeList = await fetchAlarms({ siteId: site._id, state: 'active', limit: 1000 } as any).catch(() => [] as any[]);
      if (Array.isArray(activeList)) {
        return { count: activeList.length };
      }
      return fetchAlarmCount(site._id, 'active') as Promise<{ count: number }>;
    });
    return { pv, bess, dg, grid, weather, alarms };
  };

  const setStatusChip = (key: string, isOnline: unknown) => {
    const el = getEl(key);
    if (!el) return;
    if (isOnline === true)  return applyChip(el, 'Online',  '#2e7d32');
    if (isOnline === false) return applyChip(el, 'Offline', '#d32f2f');
    applyChip(el, 'Unknown', '#6b7280');
  };

  /** PV: Online / Offline */
  const setPvStatusChip = (isOnline: unknown) => setStatusChip('pvStatus', isOnline);

  /** Generator: Running / Stopped */
  const setGenStatusChip = (isOnline: unknown) => {
    const el = getEl('genStatus');
    if (!el) return;
    if (isOnline === true)  return applyChip(el, 'Running', '#2e7d32');
    if (isOnline === false) return applyChip(el, 'Stopped', '#d32f2f');
    applyChip(el, 'Unknown', '#6b7280');
  };

  /** BESS: Charging / Discharging / Standby — power sign determines direction */
  const setBessStatusChip = (isOnline: unknown, powerKw: number | null | undefined) => {
    const el = getEl('bessStatus');
    if (!el) return;
    if (isOnline === false) return applyChip(el, 'Offline', '#d32f2f');
    if (isOnline === true) {
      const p = Number(powerKw);
      if (p > 1)   return applyChip(el, 'Discharging', '#1565c0'); // positive → delivering energy
      if (p < -1)  return applyChip(el, 'Charging',    '#e65100'); // negative → absorbing energy
      return applyChip(el, 'Standby', '#6b7280');
    }
    applyChip(el, 'Unknown', '#6b7280');
  };

  /**
   * Grid chip — uses raw GRID_STATUS register code when available:
   *   0 = Disconnected, 1 = Connected (sub-classified by power), 2 = Fault, 3 = Islanded
   * Falls back to isOnline boolean when code is not yet available.
   * power sign: positive = Importing (drawing from grid), negative = Exporting (pushing to grid)
   */
  const setGridStatusChip = (isOnline: unknown, powerKw: number | null | undefined, statusCode?: number | null) => {
    const el = getEl('gridStatus');
    if (!el) return;

    // Use the precise register code if available
    if (statusCode != null) {
      const code = Math.round(Number(statusCode));
      if (code === 0) return applyChip(el, 'Disconnected', '#d32f2f');
      if (code === 2) return applyChip(el, 'Fault',        '#b71c1c');
      if (code === 3) return applyChip(el, 'Islanded',     '#b45309');
      if (code === 1) {
        // Connected — sub-classify by power direction
        const p = Number(powerKw);
        if (p > 1)  return applyChip(el, 'Importing', '#1565c0');
        if (p < -1) return applyChip(el, 'Exporting', '#2e7d32');
        return applyChip(el, 'Connected', '#6b7280');
      }
    }

    // Fallback: no GRID_STATUS register — use isOnline + power direction
    if (isOnline === true) {
      const p = Number(powerKw);
      if (p > 1)  return applyChip(el, 'Importing', '#1565c0');
      if (p < -1) return applyChip(el, 'Exporting', '#2e7d32');
      return applyChip(el, 'Connected', '#6b7280');
    }
    if (isOnline === false) return applyChip(el, 'Disconnected', '#d32f2f');
    applyChip(el, 'Unknown', '#6b7280');
  };

  async function refreshOverview(forceFetch = false) {
    if (overviewRefreshInFlight) {
      queuedOverviewRefresh = true;
      return;
    }

    overviewRefreshInFlight = true;
    const shimmerTargets = [
      ...Array.from(container.querySelectorAll('.dashboard-v2-kpi-card')),
      ...Array.from(container.querySelectorAll('.dashboard-v2-list > div')),
      ...Array.from(container.querySelectorAll('.dashboard-panel')),
    ];
    const hasWarmSnapshot = !!getDashboardWarmSnapshot(site._id);
    const shouldShowInitialLoading = !hasWarmSnapshot && !hasShownInitialOverviewLoading;
    if (shouldShowInitialLoading) {
      shimmerTargets.forEach((el) => el.classList.add('is-loading'));
      hasShownInitialOverviewLoading = true;
    }

    try {
      const cachedSnapshot = !forceFetch ? getDashboardWarmSnapshot(site._id) : null;
      const snapshot = cachedSnapshot || await primeDashboardWarmSnapshot(site._id);
      applyDashboardSnapshot(snapshot);
    } catch (err) {
      console.warn('Dashboard overview refresh failed:', err);
    } finally {
      overviewRefreshInFlight = false;
      if (shouldShowInitialLoading) {
        clearInitialOverviewLoadingProgressively();
      }
      if (queuedOverviewRefresh && container.isConnected) {
        queuedOverviewRefresh = false;
        void refreshOverview();
      }
    }
  }

  const cachedDashboardSnapshot = getDashboardWarmSnapshot(site._id);
  if (cachedDashboardSnapshot) {
    hasShownInitialOverviewLoading = true;
    applyDashboardSnapshot(cachedDashboardSnapshot);
  }

  startDashboardWarmPolling(site._id);

  // Core trend charts (SCADA-standard overview focus)
  const chartManager = new ChartManager(site, registers);

  chartManager.createChart("sitePowerChart", [], { ...chartStyles.sitePowerLayout, showControls: true, typeControlsInHeader: true, rangeControlsInHeader: true, showDateRangeOnlyWhenFullscreen: true, showNavigatorOnlyWhenFullscreen: true, height: '100%' }, container);
  chartManager.bindRegisterToChart("sitePowerChart", [
    { assetType: "pv", metricKey: "activePower", name: "PV Power", line: { color: "#FFA500" } },
    { assetType: "battery", metricKey: "batteryPower", name: "BESS Power", line: { color: "#0ea5e9" } },
    { assetType: "generator", metricKey: "activePower", name: "Generator Power", line: { color: "#22c55e" } },
    { assetType: "grid", metricKey: "activePower", name: "Grid Power", line: { color: "#ef4444" } },
  ]);

  chartManager.createChart("irradiancePowerChart", [], { ...chartStyles.irradianceLayout, showControls: true, typeControlsInHeader: true, showDateRangeOnlyWhenFullscreen: true, showNavigatorOnlyWhenFullscreen: true, height: '100%' }, container);
  chartManager.bindRegisterToChart("irradiancePowerChart", [
    { assetType: "pv", metricKey: "irradiance", name: "Irradiance", line: { color: "#f59e0b" } },
    { assetType: "pv", metricKey: "activePower", name: "PV Power", line: { color: "#3b82f6" }, yaxis: "y2" },
  ]);

  chartManager.createChart("storageGridChart", [], { ...chartStyles.sitePowerLayout, showControls: true, typeControlsInHeader: true, showDateRangeOnlyWhenFullscreen: true, showNavigatorOnlyWhenFullscreen: true, height: '100%' }, container);
  chartManager.bindRegisterToChart("storageGridChart", [
    { assetType: "battery", metricKey: "soc", name: "BESS SOC", line: { color: "#6366f1" } },
    { assetType: "battery", metricKey: "batteryPower", name: "BESS Power", line: { color: "#06b6d4" }, yaxis: "y2" },
    { assetType: "grid", metricKey: "activePower", name: "Grid Power", line: { color: "#ef4444" }, yaxis: "y2" },
  ]);

  chartManager.createChart("gridQualityChart", [], { ...chartStyles.sitePowerLayout, showControls: true, typeControlsInHeader: true, showDateRangeOnlyWhenFullscreen: true, showNavigatorOnlyWhenFullscreen: true, height: '100%' }, container);
  chartManager.bindRegisterToChart("gridQualityChart", [
    { assetType: "grid", metricKey: "frequency", name: "Grid Frequency", line: { color: "#f97316" } },
    { assetType: "grid", metricKey: "powerFactor", name: "Grid Power Factor", line: { color: "#22c55e" } },
  ]);

  chartManager.createChart("voltageProfileChart", [], { ...chartStyles.voltageFrequencyLayout, showControls: true, typeControlsInHeader: true, showDateRangeOnlyWhenFullscreen: true, showNavigatorOnlyWhenFullscreen: true, height: '100%' }, container);
  chartManager.bindRegisterToChart("voltageProfileChart", [
    { assetType: "grid", metricKey: "voltage", name: "Grid Voltage", line: { color: "#3b82f6" } },
    { assetType: "pv", metricKey: "acVoltage", name: "PV AC Voltage", line: { color: "#a855f7" } },
  ]);

  chartManager.createChart("gensetSupportChart", [], { ...chartStyles.sitePowerLayout, showControls: true, typeControlsInHeader: true, showDateRangeOnlyWhenFullscreen: true, showNavigatorOnlyWhenFullscreen: true, height: '100%' }, container);
  chartManager.bindRegisterToChart("gensetSupportChart", [
    { assetType: "generator", metricKey: "activePower", name: "Generator Power", line: { color: "#22c55e" } },
    { assetType: "generator", metricKey: "fuelLevel", name: "Fuel Level", line: { color: "#f59e0b" }, yaxis: "y2" },
  ]);

  const setBindingsForPreset = (chartId: string, preset: string) => {
    switch (chartId) {
      case 'irradiancePowerChart': {
        const bindings = preset === 'avg'
          ? [
              { assetType: 'pv', metricKey: 'irradiance', name: 'Irradiance (Avg)', line: { color: '#f59e0b' } },
              { assetType: 'pv', metricKey: 'activePower', name: 'PV Power (Avg)', line: { color: '#3b82f6' }, yaxis: 'y2' },
            ]
          : [
              { assetType: 'pv', metricKey: 'irradiance', name: 'Irradiance', line: { color: '#f59e0b' } },
              { assetType: 'pv', metricKey: 'activePower', name: 'PV Power', line: { color: '#3b82f6' }, yaxis: 'y2' },
            ];
        chartManager.bindRegisterToChart(chartId, bindings as any);
        break;
      }
      case 'storageGridChart': {
        const bindings = preset === 'percent'
          ? [
              { assetType: 'battery', metricKey: 'soc', name: 'BESS SOC', line: { color: '#6366f1' } },
              { assetType: 'grid', metricKey: 'powerFactor', name: 'Grid PF', line: { color: '#22c55e' }, yaxis: 'y2' },
            ]
          : [
              { assetType: 'battery', metricKey: 'soc', name: 'BESS SOC', line: { color: '#6366f1' } },
              { assetType: 'battery', metricKey: 'batteryPower', name: 'BESS Power', line: { color: '#06b6d4' }, yaxis: 'y2' },
              { assetType: 'grid', metricKey: 'activePower', name: 'Grid Power', line: { color: '#ef4444' }, yaxis: 'y2' },
            ];
        chartManager.bindRegisterToChart(chartId, bindings as any);
        break;
      }
      case 'gridQualityChart': {
        const bindings = preset === 'single'
          ? [
              { assetType: 'grid', metricKey: 'frequency', name: 'Grid Frequency', line: { color: '#f97316' } },
            ]
          : [
              { assetType: 'grid', metricKey: 'frequency', name: 'Grid Frequency', line: { color: '#f97316' } },
              { assetType: 'grid', metricKey: 'powerFactor', name: 'Grid Power Factor', line: { color: '#22c55e' } },
            ];
        chartManager.bindRegisterToChart(chartId, bindings as any);
        break;
      }
      case 'voltageProfileChart': {
        const bindings = preset === 'dc'
          ? [
              { assetType: 'pv', metricKey: 'dcVoltage', name: 'PV DC Voltage', line: { color: '#a855f7' } },
              { assetType: 'battery', metricKey: 'voltage', name: 'BESS Voltage', line: { color: '#06b6d4' } },
            ]
          : [
              { assetType: 'grid', metricKey: 'voltage', name: 'Grid Voltage', line: { color: '#3b82f6' } },
              { assetType: 'pv', metricKey: 'acVoltage', name: 'PV AC Voltage', line: { color: '#a855f7' } },
            ];
        chartManager.bindRegisterToChart(chartId, bindings as any);
        break;
      }
      case 'gensetSupportChart': {
        const bindings = preset === 'fuel'
          ? [
              { assetType: 'generator', metricKey: 'fuelLevel', name: 'Fuel Level', line: { color: '#f59e0b' } },
            ]
          : [
              { assetType: 'generator', metricKey: 'activePower', name: 'Generator Power', line: { color: '#22c55e' } },
              { assetType: 'generator', metricKey: 'fuelLevel', name: 'Fuel Level', line: { color: '#f59e0b' }, yaxis: 'y2' },
            ];
        chartManager.bindRegisterToChart(chartId, bindings as any);
        break;
      }
      default:
        break;
    }
  };

  const setIntervalPreset = (chartId: string, interval: string) => {
    const mgr = chartManager as any;
    if (typeof mgr.setInterval === 'function') {
      mgr.setInterval(chartId, interval);
    }
  };

  const setControlPanelPreset = (panel: string) => {
    const list = container.querySelector('.dashboard-v2-controls .dashboard-v2-controls-list') as HTMLElement | null;
    if (list) {
      list.style.display = 'grid';
      list.style.gridTemplateColumns = 'repeat(2, minmax(0, 1fr))';
      list.style.gap = '6px';
    }

    const rows = Array.from(container.querySelectorAll('.dashboard-v2-controls .dashboard-v2-list > div')) as HTMLElement[];
    const ppcVisibleLabels = new Set([
      'real power mode',
      'real power sp / fb',
      'reactive mode',
      'reactive sp / fb',
      'pf mode',
      'pf sp / fb',
    ]);

    rows.forEach((row) => {
      const label = row.querySelector('span')?.textContent?.toLowerCase() || '';
      const isDetailRow = /sp\s*\/\s*fb|ghi/.test(label);
      if (panel === 'ppc') {
        // PPC: keep control readbacks only; hide duplicated/global snapshot rows
        row.style.display = ppcVisibleLabels.has(label) ? '' : 'none';
      } else {
        // EMS: hide SP/FB and GHI rows (shown in markup as hidden by default)
        row.style.display = isDetailRow ? 'none' : '';
      }
    });
  };

  let activeFullscreenPanel: HTMLElement | null = null;

  const ensureFullscreenButtons = () => {
    const chartIds = [
      'sitePowerChart',
      'irradiancePowerChart',
      'storageGridChart',
      'gridQualityChart',
      'voltageProfileChart',
      'gensetSupportChart',
    ];

    chartIds.forEach((chartId) => {
      const chartEl = container.querySelector(`#${chartId}`) as HTMLElement | null;
      const panel = chartEl?.closest('.dashboard-panel') as HTMLElement | null;
      const actions = panel?.querySelector('.dashboard-v2-chart-actions') as HTMLElement | null;
      if (!actions) return;
      if (actions.querySelector('[data-chart-control="fullscreen"]')) return;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'chip dashboard-v2-fullscreen-btn';
      button.dataset.chartControl = 'fullscreen';
      button.dataset.chartId = chartId;
      button.title = 'Fullscreen';
      button.innerHTML = '<span class="iconify" data-icon="mdi:fullscreen" style="font-size:14px;pointer-events:none;"></span>';
      actions.appendChild(button);
    });
  };

  const applyChartFullscreenState = () => {
    const root = container.querySelector('.dashboard-overview-v2') as HTMLElement | null;
    if (!root) return;

    root.classList.toggle('dashboard-chart-focus-active', !!activeFullscreenPanel);
    root.querySelectorAll('section').forEach((section) => {
      section.classList.remove('dashboard-chart-focus-host');
    });

    root.querySelectorAll('.dashboard-panel').forEach((panel) => {
      panel.classList.remove('dashboard-chart-focus');
    });

    container.querySelectorAll('[data-chart-control="fullscreen"]').forEach((btn) => {
      btn.classList.remove('active');
      (btn as HTMLButtonElement).title = 'Fullscreen';
      (btn as HTMLButtonElement).innerHTML = '<span class="iconify" data-icon="mdi:fullscreen" style="font-size:14px;pointer-events:none;"></span>';
    });

    if (!activeFullscreenPanel) {
      window.dispatchEvent(new Event('resize'));
      return;
    }

    const hostSection = activeFullscreenPanel.closest('section');
    hostSection?.classList.add('dashboard-chart-focus-host');
    activeFullscreenPanel.classList.add('dashboard-chart-focus');

    const actionButton = activeFullscreenPanel.querySelector('[data-chart-control="fullscreen"]') as HTMLButtonElement | null;
    if (actionButton) {
      actionButton.classList.add('active');
      actionButton.title = 'Exit Fullscreen';
      actionButton.innerHTML = '<span class="iconify" data-icon="mdi:fullscreen-exit" style="font-size:14px;pointer-events:none;"></span>';
    }

    // Reset all other fullscreen buttons to normal icon
    container.querySelectorAll('[data-chart-control="fullscreen"]').forEach((btn) => {
      if (btn !== actionButton) {
        (btn as HTMLButtonElement).title = 'Fullscreen';
        (btn as HTMLButtonElement).innerHTML = '<span class="iconify" data-icon="mdi:fullscreen" style="font-size:14px;pointer-events:none;"></span>';
      }
    });

    requestAnimationFrame(() => window.dispatchEvent(new Event('resize')));
  };

  const setChartFullscreen = (chartId: string) => {
    const chartEl = container.querySelector(`#${chartId}`) as HTMLElement | null;
    const nextPanel = chartEl?.closest('.dashboard-panel') as HTMLElement | null;
    if (!nextPanel) return;

    activeFullscreenPanel = activeFullscreenPanel === nextPanel ? null : nextPanel;
    applyChartFullscreenState();
  };

  const onDashboardKeydown = (event: KeyboardEvent) => {
    if (event.key !== 'Escape' || !activeFullscreenPanel) return;
    activeFullscreenPanel = null;
    applyChartFullscreenState();
  };

  const wireDashboardControls = () => {
    container.addEventListener('click', (event) => {
      const button = (event.target as HTMLElement)?.closest?.('.dashboard-v2-chart-actions .chip') as HTMLButtonElement | null;
      if (!button) return;

      const control = button.dataset.chartControl;
      if (control === 'fullscreen') {
        setChartFullscreen(button.dataset.chartId || '');
        return;
      }

      const actions = button.parentElement;
      if (actions) {
        actions.querySelectorAll('.chip').forEach((chip) => chip.classList.remove('active'));
      }
      button.classList.add('active');

      if (control === 'interval') {
        setIntervalPreset(button.dataset.chartId || 'sitePowerChart', button.dataset.interval || '15m');
        return;
      }
      if (control === 'preset') {
        setBindingsForPreset(button.dataset.chartId || '', button.dataset.preset || 'live');
        return;
      }
      if (control === 'panel') {
        setControlPanelPreset(button.dataset.panel || 'ems');
      }
    });

    container.addEventListener('change', (event) => {
      const target = event.target as HTMLSelectElement | null;
      if (!target) return;

      const control = target.dataset.chartControl;
      if (control !== 'type') return;

      chartManager.setChartType(target.dataset.chartId || '', target.value);
    });
  };

  ensureFullscreenButtons();
  wireDashboardControls();
  document.addEventListener('keydown', onDashboardKeydown);

  // Defer EMS default state until after container is mounted in DOM
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      setControlPanelPreset('ems');
    });
  });

  void refreshOverview();
  const pollTimer = window.setInterval(() => {
    if (!container.isConnected) {
      window.clearInterval(pollTimer);
      return;
    }
    void refreshOverview(false);
  }, 10_000);

  (container as any).cleanup = () => {
    document.removeEventListener('keydown', onDashboardKeydown);
    window.clearInterval(pollTimer);
    try {
      chartManager.cleanup();
    } catch { /* ignore */ }
  };

  return container;
}
